import React, { useEffect, useMemo, useState } from "react";
import { CustomButton } from "src/components/ui";

export interface OtpTimerProps {
  start: boolean;
  minutes: number;
  onResend: () => Promise<boolean>;
  onExpire: () => void;
  sendButtonText: string;
}

export const OtpTimer: React.FC<OtpTimerProps> = ({
  start,
  minutes,
  onResend,
  onExpire,
  sendButtonText,
}) => {
  const totalSeconds = useMemo(() => Math.max(minutes, 0) * 60, [minutes]);
  const [remainingSeconds, setRemainingSeconds] = useState(totalSeconds);
  const [isExpired, setIsExpired] = useState(false);
  const [resendInProgress, setResendInProgress] = useState(false);

  useEffect(() => {
    if (!start) {
      return;
    }
    setRemainingSeconds(totalSeconds);
    setIsExpired(false);
  }, [start, totalSeconds]);

  useEffect(() => {
    if (!start || isExpired) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          window.clearInterval(intervalId);
          setIsExpired(true);
          onExpire();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [start, isExpired, onExpire]);

  const handleResend = async () => {
    if (resendInProgress || !isExpired) return;
    setResendInProgress(true);
    try {
      const ok = await onResend();
      if (ok) {
        setRemainingSeconds(totalSeconds);
        setIsExpired(false);
      }
    } finally {
      setResendInProgress(false);
    }
  };

  const minutesDisplay = String(Math.floor(remainingSeconds / 60)).padStart(2, "0");
  const secondsDisplay = String(remainingSeconds % 60).padStart(2, "0");

  if (!start) {
    return null;
  }

  return (
    <div className="flex w-full flex-col items-center justify-center gap-2">
      {!isExpired ? (
        <span className="text-[0.875rem] text-[#485056]">
          {minutesDisplay}:{secondsDisplay}
        </span>
      ) : (
        <CustomButton
          type="primary"
          buttonSize="small"
          label={sendButtonText}
          loading={resendInProgress}
          onClick={handleResend}
          className="!text-white !bg-[#001b3a]"
        />
      )}
    </div>
  );
};

export default OtpTimer;

